'use client';

import type { ReactNode } from 'react';
import { useState } from 'react';

import classnames from 'classnames';
import {
  IoArrowForwardCircleOutline,
  IoPeopleOutline,
  IoPieChartOutline,
  IoRefreshOutline,
} from 'react-icons/io5';

import { Button } from '../../../button';
import {
  ActionDialogContent,
  Dialog,
  DialogContent,
  DialogTrigger,
} from '../../../dialog';
import { ActionDialogButtons } from '../../../dialog/layout/ActionDialogButtons';
import { IconButton } from '../../../icon-button';
import { IconTooltip } from '../../../tooltip';
import { OfficialIcon } from '../../layout/InfoIcons';
import type { AnswerRateType } from '../../multiple-choice-question-answer/MultipleChoiceQuestionAnswer.types';

import styles from './ResultActions.module.scss';

type ResultActionsProps = {
  answerRate: AnswerRateType;
  /**
   * The assessment's board, shown under the ranking icon: the standings and the
   * marks they are read against, in one. A node rather than the rows themselves —
   * the board is assembled where the assessment's metrics are read, and the kit
   * only makes room for it.
   */
  scoreboard?: ReactNode;
  official?: boolean;
  onRetry?: () => void;
};

/** What can be looked at or done about an attempt, along the foot of its result card. */
function ResultActions({
  answerRate,
  scoreboard,
  official,
  onRetry,
}: ResultActionsProps) {
  const [retryOpen, setRetryOpen] = useState(false);

  return (
    <div className={styles['main']}>
      <div className={styles['actions']}>
        {official && <OfficialIcon />}
        <IconTooltip
          icon={<IoPieChartOutline />}
          label="Desglose de respuestas"
        >
          <div className={styles['breakdown']}>
            <span
              className={classnames(styles['count'], styles['count--correct'])}
            >
              {answerRate.correct} Aciertos
            </span>
            <span
              className={classnames(
                styles['count'],
                styles['count--incorrect'],
              )}
            >
              {answerRate.incorrect} Fallos
            </span>
            <span
              className={classnames(styles['count'], styles['count--skipped'])}
            >
              {answerRate.skipped} En blanco
            </span>
          </div>
        </IconTooltip>
        {scoreboard && (
          <IconTooltip
            icon={<IoPeopleOutline />}
            label="Clasificación"
            contentFlush={true}
          >
            {scoreboard}
          </IconTooltip>
        )}
      </div>
      <div className={styles['actions']}>
        {onRetry ? (
          <Dialog open={retryOpen} onOpenChange={setRetryOpen}>
            <DialogTrigger asChild={true}>
              <IconButton
                icon={<IoRefreshOutline />}
                aria-label="Repetir simulacro"
                onClick={() => setRetryOpen((v) => !v)}
              />
            </DialogTrigger>
            <DialogContent>
              <ActionDialogContent
                title="¿Quieres volver a iniciar el simulacro?"
                subtitle="Todos los intentos quedan registrados, pero solo el primero cuenta para las clasificaciones"
                icon={<IoRefreshOutline />}
              >
                <ActionDialogButtons>
                  <Button
                    variant="outline-danger"
                    onClick={() => setRetryOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    onClick={onRetry}
                    suffixIcon={<IoArrowForwardCircleOutline />}
                  >
                    Iniciar
                  </Button>
                </ActionDialogButtons>
              </ActionDialogContent>
            </DialogContent>
          </Dialog>
        ) : (
          <IconButton
            disabled={true}
            icon={<IoRefreshOutline />}
            aria-label="Repetir simulacro"
          />
        )}
      </div>
    </div>
  );
}

export { ResultActions };
